import { IScrollViewProps, ScrollView, VStack } from 'native-base'
import React, { PropsWithChildren, useEffect, useRef } from 'react'
import { ScrollView as RNScrollView } from 'react-native'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import spacing from '../../design-system/theme/shared/spacing'
import { A11Role } from '../../lib/accessibility'
import { AlignSelf } from '../props'

import { ScreenHeading } from './ScreenHeading'

export const SCROLL_TO_BOTTOM = -1

interface ScreenProps {
  heading?: string
  before?: React.ReactNode
  space?: string
  headingAlign?: AlignSelf
  contentContainerStyle?: Partial<IScrollViewProps>
  /**
   * It will setup a padding top to the screen content to avoid the content to be hidden by the header.
   * Should be true when we are using a header and false otherwise but we are currently using this space with other purposes as well.
   */
  hasPaddingTop?: boolean
  /**
   * If true, adds the standard padding at the left and right.
   */
  addPaddingHorizontal?: boolean
  /**
   * It will remove the bottom edge of the safe area if this property is true.
   * Should be true when the bottom navigation tab is displayed.
   */
  hasTabs?: boolean
  scrollTo?: number | null
  paddingTop?: number
  scrollEnabled?: boolean
}

interface ScrollScreenProps extends ScreenProps {
  viewRef: React.MutableRefObject<RNScrollView | undefined>
  paddingTop: number
  paddingBottom: number
  scrollEnabled?: boolean
}

export const screenContentContainerStyle: Partial<IScrollViewProps> = { flexGrow: 1 }

const ScrollScreen: React.FC<PropsWithChildren<ScrollScreenProps>> = ({
  viewRef,
  paddingTop,
  paddingBottom,
  contentContainerStyle,
  children,
  scrollEnabled = true,
}) => {
  return (
    <ScrollView
      accessibilityRole={A11Role.Main}
      _contentContainerStyle={{
        pb: paddingBottom,
        // We are using pixels due to some Android / iOS inconsistencies faced.
        // It seems the ScrollView component does not handle the spacing correctly when using the native base spacing.
        pt: `${paddingTop * spacing['1']}px`,
        ...screenContentContainerStyle,
        ...contentContainerStyle,
      }}
      ref={viewRef}
      height={'100%'}
      scrollEnabled={scrollEnabled}
    >
      {children}
    </ScrollView>
  )
}

const ScreenContent: React.FC<PropsWithChildren<ScreenProps>> = ({
  children,
  addPaddingHorizontal,
  before,
  space,
  heading,
  headingAlign,
}) => {
  return (
    <VStack
      space={space}
      alignItems="stretch"
      maxWidth="600px"
      width="full"
      alignSelf="center"
      px={addPaddingHorizontal ? '4' : '0'}
      flex={1}
    >
      {before}
      {heading && <ScreenHeading align={headingAlign}>{heading}</ScreenHeading>}
      {children}
    </VStack>
  )
}

export const Screen: React.FC<PropsWithChildren<ScreenProps>> = ({
  heading,
  headingAlign = 'flex-start',
  children,
  before,
  space = '8',
  hasPaddingTop = true,
  addPaddingHorizontal = true,
  contentContainerStyle,
  hasTabs,
  scrollTo = null,
  paddingTop,
  scrollEnabled = true,
}) => {
  const viewRef = useRef<RNScrollView>()

  const insets = useSafeAreaInsets()

  useEffect(() => {
    if (viewRef.current) {
      setTimeout(() => viewRef.current?.scrollTo({ x: 0, y: 0, animated: true }))
    }
  }, [])

  useEffect(() => {
    if (viewRef.current && scrollTo !== null && scrollTo !== undefined) {
      if (scrollTo === SCROLL_TO_BOTTOM) {
        setTimeout(() => viewRef.current?.scrollToEnd({ animated: true }))
      } else {
        setTimeout(() => viewRef.current?.scrollTo({ x: 0, y: scrollTo, animated: true }))
      }
    }
  }, [scrollTo])

  const finalPaddingTop = (hasPaddingTop ? paddingTop ?? 18.5 : 0) + Math.floor(insets.top / spacing['1'])

  return (
    <ScrollScreen
      scrollEnabled={scrollEnabled}
      viewRef={viewRef}
      paddingTop={finalPaddingTop}
      paddingBottom={!hasTabs ? insets.bottom : 0}
    >
      <ScreenContent
        addPaddingHorizontal={addPaddingHorizontal}
        contentContainerStyle={contentContainerStyle}
        before={before}
        space={space}
        heading={heading}
        headingAlign={headingAlign}
      >
        {children}
      </ScreenContent>
    </ScrollScreen>
  )
}
